import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardMedia,
  Typography,
  Chip,
  Button,
  Grid,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Badge,
  LinearProgress,
  useTheme,
  alpha,
  Collapse,
  Divider,
  Stack
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Image as ImageIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Inventory as InventoryIcon,
  ShoppingCart as ShoppingCartIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Category as CategoryIcon,
  AttachMoney as MoneyIcon
} from '@mui/icons-material';
import ImageManagement from './ImageManagement';
import apiService from 'Network/apiService';
import { API_ENDPOINTS } from 'Network/apiEndpoints';
import { useToast } from 'OtherReusableFunctionality/ToastContext';
import buttonConfig from "config/ConfigAccessButton";

const ProductsList = ({ products, categories, searchQuery, selectedCategory, onRefresh, onEditProduct }) => {
  const theme = useTheme();
  const toast = useToast();
  const [expandedProduct, setExpandedProduct] = useState(null);
  const [imageManagementOpen, setImageManagementOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [productToDelete, setProductToDelete] = useState(null);

  // ============ PERMISSION FUNCTIONS - FIXED ============
  const getCurrentButtonPermissions = () => {
    const moduleConfig = buttonConfig["Product Management"];
    return moduleConfig || {};  // Return directly, no .actions property
  };
  
  const shouldEnableButton = (buttonKey) => {
    const permissions = getCurrentButtonPermissions();
    return permissions[buttonKey] === true;
  };
  // ============ END PERMISSION FUNCTIONS ============

  const filteredProducts = products;

  const handleExpandClick = (productId) => {
    setExpandedProduct(expandedProduct === productId ? null : productId);
  };

  const handleImageManagement = (product) => {
    setSelectedProduct(product);
    setImageManagementOpen(true);
  };

  const handleOpenDeleteDialog = (product) => {
    setProductToDelete(product);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setProductToDelete(null);
    setDeleteDialogOpen(false);
  };

  const getStockPercentage = (product) => {
    const remaining = parseFloat(product.remaining_quantity || 0);
    const total = parseFloat(product.analytics?.total_stock || 0);
    return total > 0 ? (remaining / total) * 100 : 0;
  };

  const formatCurrency = (amount) => {
    return `₹${parseFloat(amount || 0).toLocaleString('en-IN')}`;
  };

  const getProductImage = (product) => {
    if (!product.image) {
      return '/static/placeholder-product.png';
    }
    
    if (typeof product.image === 'string') {
      const imageUrl = product.image.split(',')[0]?.trim();
      return imageUrl || '/static/placeholder-product.png';
    }
    
    if (Array.isArray(product.image)) {
      return product.image[0] || '/static/placeholder-product.png';
    }
    
    return '/static/placeholder-product.png';
  };

  const handleConfirmDelete = async () => {
    if (!productToDelete) return;

    try {
      const res = await apiService.delete(
        `${API_ENDPOINTS.DELETE_admin_Product}?product_id=${productToDelete.id}`
      );

      if (res.status === 200) {
        toast.showSuccess('Product deleted successfully!');
        if (onRefresh) onRefresh();
      } else {
        console.error('Delete failed:', res);
        toast.showError('Failed to delete the product.');
      }
    } catch (error) {
      console.error('⚠️ Error deleting product:', error);
      toast.showError('Error deleting product.');
    } finally {
      handleCloseDeleteDialog();
    }
  };

  if (filteredProducts.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <InventoryIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
        <Typography variant="h6" color="text.secondary" gutterBottom>
          No products found
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {searchQuery ? `No products match "${searchQuery}"` : 'No products in this category'}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Grid container spacing={3}>
        {filteredProducts.map((product) => {
          const stockPercentage = getStockPercentage(product);
          const isExpanded = expandedProduct === product.id;
          const category = categories.find(c => c.id === product.category?.id);

          return (
            <Grid item xs={12} sm={6} lg={4} key={product.id}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: `0 8px 25px ${alpha(theme.palette.common.black, 0.15)}`
                  }
                }}
              >
                <CardMedia
                  component="img"
                  height="200"
                  image={getProductImage(product)}
                  alt={product.name}
                  sx={{
                    objectFit: 'cover',
                    backgroundColor: alpha(theme.palette.grey[300], 0.3)
                  }}
                />

                <CardContent sx={{ flexGrow: 1, p: 2 }}>
                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                      <Typography variant="h6" fontWeight={600} sx={{ 
                        fontSize: '1rem',
                        lineHeight: 1.3,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden'
                      }}>
                        {product.name}
                      </Typography>
                    </Box>
                    
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      SKU: {product.sku}
                    </Typography>

                    {category && (
                      <Chip
                        icon={<CategoryIcon />}
                        label={category.name}
                        variant="outlined"
                        size="small"
                        color="primary"
                      />
                    )}
                  </Box>

                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="h6" color="primary" fontWeight={700}>
                        {formatCurrency(product.farmer_price)}
                      </Typography>
                      {parseFloat(product.mrp) > parseFloat(product.farmer_price) && (
                        <Typography variant="body2" color="text.secondary" sx={{ textDecoration: 'line-through' }}>
                          {formatCurrency(product.mrp)}
                        </Typography>
                      )}
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      per {product.unit_of_measure}
                    </Typography>
                  </Box>

                  <Box sx={{ mb: 2 }}>
                  </Box>

                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={6}>
                      <Box sx={{ textAlign: 'center', p: 1, backgroundColor: alpha(theme.palette.success.main, 0.1), borderRadius: 1 }}>
                        <Typography variant="h6" color="success.main" fontWeight={600}>
                          {product.analytics?.total_sold || 0}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Sold
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Box sx={{ textAlign: 'center', p: 1, backgroundColor: alpha(theme.palette.info.main, 0.1), borderRadius: 1 }}>
                        <Typography variant="h6" color="info.main" fontWeight={600}>
                          {product.analytics?.in_cart || 0}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          In Cart
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      {/* Manage Images Button - Enabled when true, disabled when false */}
                      <Tooltip title={!shouldEnableButton('manage_image') ? "You don't have permission to manage images" : "Manage Images"}>
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => handleImageManagement(product)}
                            disabled={!shouldEnableButton('manage_image')}
                            sx={{
                              backgroundColor: alpha(theme.palette.secondary.main, 0.1),
                              color: theme.palette.secondary.main,
                              opacity: !shouldEnableButton('manage_image') ? 0.5 : 1,
                              '&:hover': {
                                backgroundColor: alpha(theme.palette.secondary.main, 0.2)
                              }
                            }}
                          >
                            <ImageIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>

                      {/* Edit Product Button - Enabled when true, disabled when false **/}
                      <Tooltip title={!shouldEnableButton('edit_product') ? "You don't have permission to edit product" : "Edit Product"}>
                        <span>
                          <IconButton
                            color="editGreen"
                            size="small"
                            onClick={() => onEditProduct && onEditProduct(product)}
                            disabled={!shouldEnableButton('edit_product')}
                            // sx={{
                            //   backgroundColor: alpha(theme.palette.primary.main, 0.1),
                            //   color: theme.palette.primary.main,
                            //   opacity: !shouldEnableButton('edit_product') ? 0.5 : 1,
                            //   '&:hover': {
                            //     backgroundColor: alpha(theme.palette.primary.main, 0.2)
                            //   }
                            // }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>

                      {/* Delete Product Button - Enabled when true, disabled when false */}
                      <Tooltip title={!shouldEnableButton('delete_product') ? "You don't have permission to delete product" : "Delete Product"}>
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => handleOpenDeleteDialog(product)}
                            disabled={!shouldEnableButton('delete_product')}
                            sx={{
                              backgroundColor: alpha(theme.palette.error.main, 0.1),
                              color: theme.palette.error.main,
                              opacity: !shouldEnableButton('delete_product') ? 0.5 : 1,
                              '&:hover': {
                                backgroundColor: alpha(theme.palette.error.main, 0.2)
                              }
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>

                    <Button
                      size="small"
                      onClick={() => handleExpandClick(product.id)}
                      endIcon={isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      sx={{ textTransform: 'none' }}
                    >
                      {isExpanded ? 'Less' : 'More'}
                    </Button>
                  </Box>

                  <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                    <Divider sx={{ my: 2 }} />
                    
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                        Description
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden'
                      }}>
                        {product.description || 'No description available'}
                      </Typography>
                    </Box>

                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">
                          HSN Code
                        </Typography>
                        <Typography variant="body2" fontWeight={500}>
                          {product.hsn_code || 'N/A'}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">
                          GST Rate
                        </Typography>
                        <Typography variant="body2" fontWeight={500}>
                          {product.gst}%
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">
                          Brand
                        </Typography>
                        <Typography variant="body2" fontWeight={500}>
                          {product.brand?.name || 'N/A'}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">
                          Vendor
                        </Typography>
                        <Typography variant="body2" fontWeight={500}>
                          {product.vendor?.name || 'N/A'}
                        </Typography>
                      </Grid>
                    </Grid>

                    {(product.ingredients && product.ingredients !== 'nan') && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                          Ingredients
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {product.ingredients}
                        </Typography>
                      </Box>
                    )}

                    {(product.shelf_life && product.shelf_life !== 'nan') && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                          Shelf Life
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {product.shelf_life}
                        </Typography>
                      </Box>
                    )}
                  </Collapse>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      <Dialog open={deleteDialogOpen} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete{' '}
            <strong>{productToDelete?.name || 'this product'}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} color="inherit">
            Cancel
          </Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <ImageManagement
        open={imageManagementOpen}
        onClose={() => setImageManagementOpen(false)}
        product={selectedProduct}
        onImageUpdated={onRefresh}
      />
    </Box>
  );
};

export default ProductsList;